import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger
} from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from '@/components/ui/collapsible';
import {
  Bot,
  TrendingUp,
  DollarSign,
  Clock,
  AlertCircle,
  CheckCircle,
  Save,
  RotateCcw,
  History,
  TestTube,
  Info,
  Users,
  Loader2,
  Building,
  ChevronDown,
  FileText
} from 'lucide-react';
import {
  getAgentConfigs,
  updateAgentConfig,
  createPromptVersion,
  activatePromptVersion,
  testAgentConfiguration
} from '@/services/aiAgentService';
import { getDocuments } from '@/services/aiDocumentService';
import type { AgentConfig, AgentType, AIDocument } from '@/types/ai-admin';
import { toast } from 'sonner';
import { createComponentLogger } from '@/utils/logger';

const log = createComponentLogger('AIAgentSettings', 'ai-agents');

interface AgentTestResult {
  response: string;
  responseTime: number;
  tokensInput: number;
  tokensOutput: number;
  cost: number;
  model: string;
  documentsUsed: Array<{
    title: string;
    relevance: number;
  }>;
}


// Agent type descriptions
const agentDescriptions: Record<AgentType, string> = {
  encompass_helper: 'Specialized for Encompass API questions and troubleshooting',
  pre_approval: 'Guides through pre-approval process and requirements',
  document_checklist: 'Manages loan document requirements and tracking',
  mortgage_education: 'Provides educational content about mortgage processes',
  investor_guidelines: 'Answers questions about investor overlays and guidelines',
  wisr: 'Intelligent assistant with broad knowledge base access',
  general: 'General purpose mortgage and lending assistant'
};

// Model display configuration
const MODEL_DISPLAY_NAMES: Record<string, string> = {
  'claude-haiku-4-5-20251001': 'Claude Haiku 4.5',
  'claude-sonnet-4-5-20250929': 'Claude Sonnet 4.5',
  'claude-opus-4-1-20250805': 'Claude Opus 4.1'
};

const MODEL_DESCRIPTIONS: Record<string, string> = {
  'claude-haiku-4-5-20251001': 'Fast and cost-effective for simple tasks',
  'claude-sonnet-4-5-20250929': 'Balanced performance for complex reasoning',
  'claude-opus-4-1-20250805': 'Most capable for expert-level tasks'
};

const MODEL_COSTS: Record<string, string> = {
  'claude-haiku-4-5-20251001': '$1.00/$5.00',
  'claude-sonnet-4-5-20250929': '$3.00/$15.00',
  'claude-opus-4-1-20250805': '$15.00/$75.00'
};

export default function AIAgentSettings() {
  const [agentConfigs, setAgentConfigs] = useState<AgentConfig[]>([]);
  const [selectedAgent, setSelectedAgent] = useState<AgentConfig | null>(null);
  const [documents, setDocuments] = useState<AIDocument[]>([]);
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  const [showTestDialog, setShowTestDialog] = useState(false);
  const [testQuery, setTestQuery] = useState('');
  const [testResult, setTestResult] = useState<AgentTestResult | null>(null);
  const [isTesting, setIsTesting] = useState(false);
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showSaveVersionDialog, setShowSaveVersionDialog] = useState(false);
  const [versionNotes, setVersionNotes] = useState('');
  const [isSavingVersion, setIsSavingVersion] = useState(false);
  const [expandedInvestors, setExpandedInvestors] = useState<Set<string>>(new Set());

  // Group documents by investor for the Documents tab
  const documentsByInvestor = useMemo(() => {
    const groups: Record<string, AIDocument[]> = {};

    documents.forEach(doc => {
      const key = doc.investorName || 'Other Documents';
      if (!groups[key]) {
groups[key] = [];
}
      groups[key].push(doc);
    });

    // Sort investor groups: alphabetically, with "Other Documents" at the end
    return Object.entries(groups).sort(([a], [b]) =>
      a === 'Other Documents' ? 1 : b === 'Other Documents' ? -1 : a.localeCompare(b)
    );
  }, [documents]);

  // Toggle all documents for an investor
  const handleInvestorToggle = (investorDocs: AIDocument[], enabled: boolean) => {
    if (!selectedAgent) {
return;
}

    const docIds = investorDocs.map(d => d.id);
    const current = selectedAgent.enabledDocuments || [];

    const updated = enabled
      ? [...new Set([...current, ...docIds])]
      : current.filter(id => !docIds.includes(id));

    setSelectedAgent({ ...selectedAgent, enabledDocuments: updated });
    setUnsavedChanges(true);
  };

  // Load data from database on mount
  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    try {
      setIsLoading(true);

      // Load agent configurations
      const agents = await getAgentConfigs();
      setAgentConfigs(agents);

      // Load documents
      const docs = await getDocuments();
      setDocuments(docs);

      // Select the first active agent by default
      const activeAgent = agents.find(a => a.isActive);
      if (activeAgent) {
        setSelectedAgent(activeAgent);
      }

      log.info('Agent settings loaded', { agentCount: agents.length, docCount: docs.length });
    } catch (error) {
      log.error('Failed to load agent settings', {}, error as Error);
      toast.error('Failed to load agent settings');
    } finally {
      setIsLoading(false);
    }
  }

  // Handle agent selection
  const handleAgentSelect = (agentId: string) => {
    if (unsavedChanges) {
      // Keep the existing native discard confirmation until this settings screen adopts a shared alert dialog.
      // eslint-disable-next-line no-alert
      if (!confirm('You have unsaved changes. Do you want to discard them?')) {
        return;
      }
    }
    const agent = agentConfigs.find(a => a.id === agentId);
    if (agent) {
      setSelectedAgent({ ...agent });
      setUnsavedChanges(false);
    }
  };

  // Handle saving changes
  const handleSave = async () => {
    if (selectedAgent) {
      try {
        const updated = await updateAgentConfig(selectedAgent.id, selectedAgent);
        const updatedConfigs = agentConfigs.map(a =>
          a.id === selectedAgent.id ? updated : a
        );
        setAgentConfigs(updatedConfigs);
        setSelectedAgent(updated);
        setUnsavedChanges(false);
        toast.success('Agent configuration saved');
        log.info('Agent configuration saved', { agentId: selectedAgent.id });
      } catch (error) {
        log.error('Failed to save agent configuration', { agentId: selectedAgent.id }, error as Error);
        toast.error('Failed to save configuration');
      }
    }
  };

  // Handle prompt version change (switch to a different version)
  const handlePromptVersionChange = async (version: number) => {
    if (!selectedAgent) {
return;
}

    const targetVersion = selectedAgent.promptVersions.find(v => v.version === version);
    if (!targetVersion) {
return;
}

    try {
      await activatePromptVersion(selectedAgent.id, targetVersion.id);

      // Update local state with the version's prompt
      setSelectedAgent({
        ...selectedAgent,
        currentPromptVersion: version,
        systemPrompt: targetVersion.prompt
      });

      // Reload to get fresh data
      await loadData();
      toast.success(`Switched to Version ${version}`);
      log.info('Prompt version activated', { agentId: selectedAgent.id, version });
    } catch (error) {
      log.error('Failed to switch version', { agentId: selectedAgent.id, version }, error as Error);
      toast.error('Failed to switch version');
    }
  };

  // Handle saving current prompt as a new version
  const handleSaveVersion = async () => {
    if (!selectedAgent) {
return;
}

    setIsSavingVersion(true);
    try {
      const newVersion = await createPromptVersion(
        selectedAgent.id,
        selectedAgent.systemPrompt,
        versionNotes || undefined
      );

      // Also activate this new version
      await activatePromptVersion(selectedAgent.id, newVersion.id);

      // Reload data to get updated versions list
      await loadData();

      setShowSaveVersionDialog(false);
      setVersionNotes('');
      toast.success(`Saved as Version ${newVersion.version}`);
      log.info('Prompt version saved', { agentId: selectedAgent.id, version: newVersion.version });
    } catch (error) {
      log.error('Failed to save version', { agentId: selectedAgent.id }, error as Error);
      toast.error('Failed to save version');
    } finally {
      setIsSavingVersion(false);
    }
  };

  // Handle document selection
  const handleDocumentToggle = (docId: string) => {
    if (selectedAgent) {
      const enabledDocs = selectedAgent.enabledDocuments || [];
      const updated = enabledDocs.includes(docId)
        ? enabledDocs.filter(id => id !== docId)
        : [...enabledDocs, docId];

      setSelectedAgent({
        ...selectedAgent,
        enabledDocuments: updated
      });
      setUnsavedChanges(true);
    }
  };

  // Handle test query
  const handleTestQuery = async () => {
    if (!selectedAgent || !testQuery) {
return;
}

    setIsTesting(true);
    try {
      const result = await testAgentConfiguration(selectedAgent.id, testQuery);
      setTestResult({
        ...result,
        model: selectedAgent.model
      });
      log.info('Test query completed', { agentId: selectedAgent.id });
    } catch (error) {
      log.error('Test query failed', { agentId: selectedAgent.id }, error as Error);
      toast.error('Test query failed');
    } finally {
      setIsTesting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center space-y-4">
          <Loader2 className="h-8 w-8 text-muted-foreground mx-auto animate-spin" />
          <p className="text-muted-foreground">Loading agent configurations...</p>
        </div>
      </div>
    );
  }

  if (!selectedAgent) {
    return (
      <div className="flex items-center justify-center h-96">
        <p className="text-muted-foreground">Select an agent to configure</p>
      </div>
    );
  }

  return (
    <div className="grid gap-6 lg:grid-cols-4">
      {/* Agent List Sidebar */}
      <Card className="lg:col-span-1">
        <CardHeader>
          <CardTitle>AI Agents</CardTitle>
          <CardDescription>
            Select an agent to configure
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <ScrollArea className="h-[600px]">
            <div className="space-y-1 p-4">
              {agentConfigs.map((agent) => (
                <button
                  key={agent.id}
                  onClick={() => handleAgentSelect(agent.id)}
                  className={`w-full text-left p-3 rounded-lg transition-colors ${
                    selectedAgent.id === agent.id
                      ? 'bg-primary text-primary-foreground'
                      : 'hover:bg-accent'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Bot className="h-4 w-4" />
                      <span className="font-medium">{agent.name}</span>
                    </div>
                    {agent.isActive ? (
                      <Badge variant="default" className="h-5">
                        <CheckCircle className="mr-1 h-3 w-3" />
                        Active
                      </Badge>
                    ) : (
                      <Badge variant="secondary" className="h-5">
                        Inactive
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs mt-1 opacity-80 line-clamp-1">
                    {agentDescriptions[agent.agentType]}
                  </p>
                  <div className="flex items-center space-x-2 mt-2">
                    <Badge variant="outline" className="text-xs">
                      {agent.stats.totalQueries} queries
                    </Badge>
                    {agent.stats.avgUserRating && (
                      <Badge variant="outline" className="text-xs">
                        ⭐ {agent.stats.avgUserRating.toFixed(1)}
                      </Badge>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </ScrollArea>
        </CardContent>
      </Card>

      {/* Main Configuration Area */}
      <div className="lg:col-span-3 space-y-6">
        {/* Agent Header */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <Bot className="h-8 w-8 text-primary" />
                <div>
                  <CardTitle className="text-2xl">{selectedAgent.name}</CardTitle>
                  <CardDescription>{selectedAgent.description}</CardDescription>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Switch
                  checked={selectedAgent.isActive}
                  onCheckedChange={(checked) => {
                    setSelectedAgent({ ...selectedAgent, isActive: checked });
                    setUnsavedChanges(true);
                  }}
                />
                <Label>Active</Label>
              </div>
            </div>
          </CardHeader>
          {unsavedChanges && (
            <CardContent>
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Unsaved Changes</AlertTitle>
                <AlertDescription>
                  You have unsaved changes. Click Save to apply them.
                </AlertDescription>
              </Alert>
            </CardContent>
          )}
        </Card>

        {/* Configuration Tabs */}
        <Tabs defaultValue="model" className="space-y-4">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="model">Model</TabsTrigger>
            <TabsTrigger value="prompts">Prompts</TabsTrigger>
            <TabsTrigger value="documents">Documents</TabsTrigger>
            <TabsTrigger value="advanced">Advanced</TabsTrigger>
            <TabsTrigger value="stats">Statistics</TabsTrigger>
          </TabsList>

          {/* Model Configuration Tab */}
          <TabsContent value="model" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Model Configuration</CardTitle>
                <CardDescription>
                  Select the AI model and configure its parameters
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Model Configuration - Dynamic based on agent config */}
                <div className="space-y-4">
                  <Label>AI Model</Label>
                  <Select
                    value={selectedAgent.model}
                    onValueChange={(value) => {
                      setSelectedAgent({ ...selectedAgent, model: value as typeof selectedAgent.model });
                      setUnsavedChanges(true);
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="claude-haiku-4-5-20251001">
                        Claude Haiku 4.5 — $1/$5 per 1M tokens
                      </SelectItem>
                      <SelectItem value="claude-sonnet-4-5-20250929">
                        Claude Sonnet 4.5 — $3/$15 per 1M tokens
                      </SelectItem>
                      <SelectItem value="claude-opus-4-1-20250805">
                        Claude Opus 4.1 — $15/$75 per 1M tokens
                      </SelectItem>
                    </SelectContent>
                  </Select>

                  {/* Model details card */}
                  <div className="rounded-lg border bg-accent p-4">
                    <div className="flex items-start justify-between">
                      <div className="space-y-1">
                        <div className="flex items-center space-x-2">
                          <h4 className="font-medium">{MODEL_DISPLAY_NAMES[selectedAgent.model] || selectedAgent.model}</h4>
                          <Badge variant="default" className="h-5">Active</Badge>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {MODEL_DESCRIPTIONS[selectedAgent.model] || 'AI model'}
                        </p>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-medium">{MODEL_COSTS[selectedAgent.model] || 'N/A'}</div>
                        <div className="text-xs text-muted-foreground">per 1M tokens</div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-4 mt-3">
                      <Badge variant="secondary">200k context</Badge>
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Temperature Slider */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label>Temperature</Label>
                    <span className="text-sm text-muted-foreground">
                      {selectedAgent.temperature}
                    </span>
                  </div>
                  <Slider
                    value={[selectedAgent.temperature]}
                    onValueChange={([value]) => {
                      setSelectedAgent({ ...selectedAgent, temperature: value });
                      setUnsavedChanges(true);
                    }}
                    min={0}
                    max={1}
                    step={0.1}
                    className="w-full"
                  />
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>Focused (0.0)</span>
                    <span>Balanced (0.5)</span>
                    <span>Creative (1.0)</span>
                  </div>
                </div>

                {/* Max Tokens */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label>Maximum Tokens</Label>
                    <span className="text-sm text-muted-foreground">
                      {selectedAgent.maxTokens}
                    </span>
                  </div>
                  <Slider
                    value={[selectedAgent.maxTokens]}
                    onValueChange={([value]) => {
                      setSelectedAgent({ ...selectedAgent, maxTokens: value });
                      setUnsavedChanges(true);
                    }}
                    min={256}
                    max={4096}
                    step={256}
                    className="w-full"
                  />
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>Concise (256)</span>
                    <span>Standard (2048)</span>
                    <span>Detailed (4096)</span>
                  </div>
                </div>

                {/* Cost Estimate */}
                <Alert>
                  <DollarSign className="h-4 w-4" />
                  <AlertTitle>Estimated Cost</AlertTitle>
                  <AlertDescription>
                    Based on current settings with Claude Haiku 4.5:
                    <ul className="mt-2 space-y-1">
                      <li>• Average query: ${(selectedAgent.stats.avgCost || 0.003).toFixed(4)}</li>
                      <li>• Monthly projection: ${((selectedAgent.stats.avgCost || 0.003) * 30 * 50).toFixed(2)}</li>
                      <li>• With prompt caching: ${((selectedAgent.stats.avgCost || 0.003) * 0.1 * 30 * 50).toFixed(2)} (90% savings)</li>
                    </ul>
                  </AlertDescription>
                </Alert>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Prompts Tab */}
          <TabsContent value="prompts" className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>System Prompt</CardTitle>
                    <CardDescription>
                      Configure the agent's behavior and instructions
                    </CardDescription>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowHistoryDialog(true)}
                  >
                    <History className="mr-2 h-4 w-4" />
                    Version History
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Prompt Version Selector */}
                <div className="flex items-center space-x-4">
                  <Label>Active Version</Label>
                  <Select
                    value={selectedAgent.currentPromptVersion.toString()}
                    onValueChange={(value) => handlePromptVersionChange(parseInt(value))}
                  >
                    <SelectTrigger className="w-[200px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {selectedAgent.promptVersions.map((version) => (
                        <SelectItem key={version.id} value={version.version.toString()}>
                          Version {version.version}
                          {version.isActive && ' (Current)'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowSaveVersionDialog(true)}
                  >
                    <Save className="mr-2 h-4 w-4" />
                    Save Version
                  </Button>
                </div>

                {/* Prompt Editor */}
                <div className="space-y-2">
                  <Label>System Prompt</Label>
                  <Textarea
                    value={selectedAgent.systemPrompt}
                    onChange={(e) => {
                      setSelectedAgent({ ...selectedAgent, systemPrompt: e.target.value });
                      setUnsavedChanges(true);
                    }}
                    className="min-h-[400px] font-mono text-sm"
                    placeholder="Enter the system prompt..."
                  />
                </div>

                {/* Prompt Templates */}
                <div className="space-y-2">
                  <Label>Quick Templates</Label>
                  <div className="grid grid-cols-2 gap-2">
                    <Button variant="outline" size="sm">
                      Chain-of-Thought
                    </Button>
                    <Button variant="outline" size="sm">
                      Few-Shot Examples
                    </Button>
                    <Button variant="outline" size="sm">
                      Role-Based
                    </Button>
                    <Button variant="outline" size="sm">
                      Structured Output
                    </Button>
                  </div>
                </div>

                {/* Test Prompt */}
                <div className="flex justify-end">
                  <Button onClick={() => setShowTestDialog(true)}>
                    <TestTube className="mr-2 h-4 w-4" />
                    Test Prompt
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Documents Tab */}
          <TabsContent value="documents" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Document Selection</CardTitle>
                <CardDescription>
                  Choose which documents this agent can access for RAG retrieval
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Switch
                        checked={selectedAgent.enabledDocuments?.length === documents.length}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setSelectedAgent({
                              ...selectedAgent,
                              enabledDocuments: documents.map(d => d.id)
                            });
                          } else {
                            setSelectedAgent({
                              ...selectedAgent,
                              enabledDocuments: []
                            });
                          }
                          setUnsavedChanges(true);
                        }}
                      />
                      <Label>Select All Documents</Label>
                    </div>
                    <Badge variant="outline">
                      {selectedAgent.enabledDocuments?.length || 0} / {documents.length} selected
                    </Badge>
                  </div>

                  <Separator />

                  <ScrollArea className="h-[400px]">
                    <div className="space-y-3">
                      {documentsByInvestor.map(([investorName, docs]) => {
                        const enabledDocs = selectedAgent.enabledDocuments || [];
                        const allSelected = docs.every(d => enabledDocs.includes(d.id));
                        const someSelected = docs.some(d => enabledDocs.includes(d.id));
                        const isExpanded = expandedInvestors.has(investorName);
                        const selectedCount = docs.filter(d => enabledDocs.includes(d.id)).length;

                        return (
                          <Collapsible
                            key={investorName}
                            open={isExpanded}
                            onOpenChange={(open) => {
                              const next = new Set(expandedInvestors);
                              if (open) {
                                next.add(investorName);
                              } else {
                                next.delete(investorName);
                              }
                              setExpandedInvestors(next);
                            }}
                          >
                            <div className="flex items-center gap-2 p-2 bg-muted/50 rounded-lg border">
                              <input
                                type="checkbox"
                                checked={allSelected}
                                ref={(el) => {
                                  if (el) {
el.indeterminate = someSelected && !allSelected;
}
                                }}
                                onChange={(e) => handleInvestorToggle(docs, e.target.checked)}
                                className="h-4 w-4"
                              />
                              <CollapsibleTrigger className="flex-1 flex items-center gap-2 text-left">
                                {investorName === 'Other Documents' ? (
                                  <FileText className="h-4 w-4 text-muted-foreground" />
                                ) : (
                                  <Building className="h-4 w-4 text-muted-foreground" />
                                )}
                                <span className="font-medium">{investorName}</span>
                                <Badge variant="secondary" className="text-xs">
                                  {selectedCount}/{docs.length} docs
                                </Badge>
                                <ChevronDown className={`h-4 w-4 ml-auto transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                              </CollapsibleTrigger>
                            </div>
                            <CollapsibleContent className="pl-6 mt-1 space-y-1">
                              {docs.map((doc) => (
                                <div
                                  key={doc.id}
                                  className="flex items-center justify-between p-2 rounded-md hover:bg-muted/30"
                                >
                                  <div className="flex items-center space-x-3">
                                    <input
                                      type="checkbox"
                                      checked={enabledDocs.includes(doc.id)}
                                      onChange={() => handleDocumentToggle(doc.id)}
                                      className="h-4 w-4"
                                    />
                                    <div>
                                      <p className="text-sm font-medium">{doc.title}</p>
                                      <div className="flex items-center space-x-2 mt-0.5">
                                        <Badge variant="outline" className="text-xs">
                                          {doc.category || doc.documentType}
                                        </Badge>
                                        <span className="text-xs text-muted-foreground">
                                          {doc.chunks.length} chunks
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="text-right text-xs text-muted-foreground">
                                    <p>{doc.citationCount} citations</p>
                                    <p>
                                      {doc.relevanceScore !== null && doc.relevanceScore !== undefined
                                        ? `${(doc.relevanceScore * 100).toFixed(0)}%`
                                        : 'N/A'} relevance
                                    </p>
                                  </div>
                                </div>
                              ))}
                            </CollapsibleContent>
                          </Collapsible>
                        );
                      })}
                    </div>
                  </ScrollArea>

                  <Alert>
                    <Info className="h-4 w-4" />
                    <AlertTitle>Document Priority</AlertTitle>
                    <AlertDescription>
                      Documents with higher citation counts and relevance scores will be prioritized during retrieval.
                    </AlertDescription>
                  </Alert>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Advanced Tab */}
          <TabsContent value="advanced" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>RAG Configuration</CardTitle>
                <CardDescription>
                  Fine-tune retrieval and generation settings
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Top K */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Top K Documents</Label>
                    <span className="text-sm text-muted-foreground">
                      {selectedAgent.ragConfig.topK}
                    </span>
                  </div>
                  <Slider
                    value={[selectedAgent.ragConfig.topK]}
                    onValueChange={([value]) => {
                      setSelectedAgent({
                        ...selectedAgent,
                        ragConfig: { ...selectedAgent.ragConfig, topK: value }
                      });
                      setUnsavedChanges(true);
                    }}
                    min={1}
                    max={10}
                    step={1}
                  />
                </div>

                {/* Similarity Threshold */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Similarity Threshold</Label>
                    <span className="text-sm text-muted-foreground">
                      {selectedAgent.ragConfig.similarityThreshold}
                    </span>
                  </div>
                  <Slider
                    value={[selectedAgent.ragConfig.similarityThreshold]}
                    onValueChange={([value]) => {
                      setSelectedAgent({
                        ...selectedAgent,
                        ragConfig: { ...selectedAgent.ragConfig, similarityThreshold: value }
                      });
                      setUnsavedChanges(true);
                    }}
                    min={0.3}
                    max={1}
                    step={0.05}
                  />
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>Broader (0.3)</span>
                    <span>Stricter (1.0)</span>
                  </div>
                </div>

                <Separator />

                {/* Search Weights */}
                <div className="space-y-4">
                  <h4 className="text-sm font-medium">Hybrid Search Weights</h4>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>Semantic Weight</Label>
                      <span className="text-sm text-muted-foreground">
                        {(selectedAgent.ragConfig.semanticWeight * 100).toFixed(0)}%
                      </span>
                    </div>
                    <Slider
                      value={[selectedAgent.ragConfig.semanticWeight]}
                      onValueChange={([value]) => {
                        setSelectedAgent({
                          ...selectedAgent,
                          ragConfig: {
                            ...selectedAgent.ragConfig,
                            semanticWeight: value,
                            keywordWeight: 1 - value
                          }
                        });
                        setUnsavedChanges(true);
                      }}
                      min={0}
                      max={1}
                      step={0.05}
                    />
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>Keyword Weight</Label>
                      <span className="text-sm text-muted-foreground">
                        {(selectedAgent.ragConfig.keywordWeight * 100).toFixed(0)}%
                      </span>
                    </div>
                    <div className="h-2 bg-secondary rounded-full overflow-hidden">
                      <div
                        className="h-full bg-primary transition-all"
                        style={{ width: `${selectedAgent.ragConfig.keywordWeight * 100}%` }}
                      />
                    </div>
                  </div>
                </div>

              </CardContent>
            </Card>
          </TabsContent>

          {/* Statistics Tab */}
          <TabsContent value="stats" className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Total Queries</CardTitle>
                  <Users className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {selectedAgent.stats.totalQueries.toLocaleString()}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Last used {selectedAgent.stats.lastUsedAt
                      ? new Date(selectedAgent.stats.lastUsedAt).toLocaleDateString()
                      : 'Never'}
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Average Cost</CardTitle>
                  <DollarSign className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    ${selectedAgent.stats.avgCost.toFixed(4)}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Per query
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Response Time</CardTitle>
                  <Clock className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {(selectedAgent.stats.avgResponseTime / 1000).toFixed(1)}s
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Average
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Success Rate</CardTitle>
                  <TrendingUp className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {selectedAgent.stats.successRate.toFixed(1)}%
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {selectedAgent.stats.avgUserRating
                      ? `⭐ ${selectedAgent.stats.avgUserRating.toFixed(1)} rating`
                      : 'No ratings yet'}
                  </p>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader>
                <CardTitle>Token Usage</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium">Input Tokens</span>
                      <span className="text-sm text-muted-foreground">
                        {selectedAgent.stats.avgTokensInput} avg
                      </span>
                    </div>
                    <div className="h-2 bg-secondary rounded-full overflow-hidden">
                      <div
                        className="h-full bg-blue-500"
                        style={{
                          width: `${(selectedAgent.stats.avgTokensInput / 2048) * 100}%`
                        }}
                      />
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium">Output Tokens</span>
                      <span className="text-sm text-muted-foreground">
                        {selectedAgent.stats.avgTokensOutput} avg
                      </span>
                    </div>
                    <div className="h-2 bg-secondary rounded-full overflow-hidden">
                      <div
                        className="h-full bg-green-500"
                        style={{
                          width: `${(selectedAgent.stats.avgTokensOutput / selectedAgent.maxTokens) * 100}%`
                        }}
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Performance Metrics by Version */}
            {selectedAgent.promptVersions.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Version Performance</CardTitle>
                  <CardDescription>
                    Comparison of different prompt versions
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Version</TableHead>
                        <TableHead>Queries</TableHead>
                        <TableHead>Avg Rating</TableHead>
                        <TableHead>Avg Time</TableHead>
                        <TableHead>Avg Cost</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selectedAgent.promptVersions.map((version) => (
                        <TableRow key={version.id}>
                          <TableCell>v{version.version}</TableCell>
                          <TableCell>
                            {version.performanceMetrics?.queryCount || 0}
                          </TableCell>
                          <TableCell>
                            {version.performanceMetrics?.avgRating
                              ? `⭐ ${version.performanceMetrics.avgRating.toFixed(1)}`
                              : 'N/A'}
                          </TableCell>
                          <TableCell>
                            {version.performanceMetrics?.avgResponseTime
                              ? `${(version.performanceMetrics.avgResponseTime / 1000).toFixed(1)}s`
                              : 'N/A'}
                          </TableCell>
                          <TableCell>
                            {version.performanceMetrics?.avgCost
                              ? `$${version.performanceMetrics.avgCost.toFixed(4)}`
                              : 'N/A'}
                          </TableCell>
                          <TableCell>
                            {version.isActive && (
                              <Badge variant="default">Active</Badge>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>

        {/* Action Buttons */}
        <Card>
          <CardContent className="flex justify-between items-center pt-6">
            <Button
              variant="outline"
              onClick={() => {
                setSelectedAgent(
                  agentConfigs.find(a => a.id === selectedAgent.id)!
                );
                setUnsavedChanges(false);
              }}
              disabled={!unsavedChanges}
            >
              <RotateCcw className="mr-2 h-4 w-4" />
              Reset Changes
            </Button>
            <div className="flex space-x-2">
              <Button
                variant="outline"
                onClick={() => setShowTestDialog(true)}
              >
                <TestTube className="mr-2 h-4 w-4" />
                Test Configuration
              </Button>
              <Button
                onClick={handleSave}
                disabled={!unsavedChanges}
              >
                <Save className="mr-2 h-4 w-4" />
                Save Configuration
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Test Dialog */}
      <Dialog open={showTestDialog} onOpenChange={setShowTestDialog}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Test Agent Configuration</DialogTitle>
            <DialogDescription>
              Test the current configuration with a sample query
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Test Query</Label>
              <Textarea
                value={testQuery}
                onChange={(e) => setTestQuery(e.target.value)}
                placeholder="Enter a test question for the agent..."
                className="min-h-[100px]"
              />
            </div>

            {testResult && (
              <div className="space-y-4">
                <Separator />
                <div className="space-y-2">
                  <Label>Response</Label>
                  <Card>
                    <CardContent className="pt-4">
                      <p className="text-sm">{testResult.response}</p>
                    </CardContent>
                  </Card>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Response Time</Label>
                    <p className="text-sm text-muted-foreground">
                      {testResult.responseTime}ms
                    </p>
                  </div>
                  <div>
                    <Label>Tokens</Label>
                    <p className="text-sm text-muted-foreground">
                      {testResult.tokensInput} in / {testResult.tokensOutput} out
                    </p>
                  </div>
                  <div>
                    <Label>Cost</Label>
                    <p className="text-sm text-muted-foreground">
                      ${testResult.cost.toFixed(4)}
                    </p>
                  </div>
                </div>

                {testResult.documentsUsed && (
                  <div className="space-y-2">
                    <Label>Documents Used</Label>
                    <div className="space-y-1">
                      {testResult.documentsUsed.map((doc, index) => (
                        <div key={index} className="flex items-center justify-between text-sm">
                          <span>{doc.title}</span>
                          <Badge variant="outline">
                            {(doc.relevance * 100).toFixed(0)}% relevance
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTestDialog(false)}>
              Close
            </Button>
            <Button onClick={handleTestQuery} disabled={!testQuery || isTesting}>
              {isTesting ? 'Testing...' : 'Run Test'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Version History Dialog */}
      <Dialog open={showHistoryDialog} onOpenChange={setShowHistoryDialog}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Prompt Version History</DialogTitle>
            <DialogDescription>
              View and compare different versions of the system prompt
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="h-[400px]">
            <div className="space-y-4">
              {selectedAgent.promptVersions.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <p>No versions saved yet.</p>
                  <p className="text-sm mt-2">Click "Save Version" to create your first snapshot.</p>
                </div>
              ) : (
                selectedAgent.promptVersions.map((version) => (
                  <Card key={version.id}>
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <CardTitle className="text-sm">
                            Version {version.version}
                          </CardTitle>
                          {version.isActive && (
                            <Badge variant="default">Active</Badge>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {new Date(version.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground mb-2 whitespace-pre-wrap">
                        {version.prompt}
                      </p>
                      {version.performanceMetrics && (
                        <div className="flex items-center space-x-4 text-xs">
                          <span>{version.performanceMetrics.queryCount} queries</span>
                          <span>⭐ {version.performanceMetrics.avgRating.toFixed(1)}</span>
                          <span>${version.performanceMetrics.avgCost.toFixed(4)}/query</span>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Save Version Dialog */}
      <Dialog open={showSaveVersionDialog} onOpenChange={setShowSaveVersionDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Save Prompt Version</DialogTitle>
            <DialogDescription>
              Save the current prompt as a new version. This creates a snapshot you can return to later.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Version Notes (optional)</Label>
              <Textarea
                value={versionNotes}
                onChange={(e) => setVersionNotes(e.target.value)}
                placeholder="What changes did you make? e.g., 'Added installation troubleshooting section'"
                className="min-h-[100px]"
              />
            </div>
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                This will save the current prompt as Version {(selectedAgent.promptVersions.length || 0) + 1} and make it the active version.
              </AlertDescription>
            </Alert>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSaveVersionDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveVersion} disabled={isSavingVersion}>
              {isSavingVersion ? 'Saving...' : 'Save Version'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
